import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { TICKET_STATUSES, type TicketStatus } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card, StatusBadge } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { invalidateHiddenStatuses } from '../hooks/useHiddenStatuses.ts';

interface Settings {
  ticketingEmail: string | null;
  hiddenTicketStatuses: TicketStatus[];
  staleWaitingEnabled: boolean;
  staleWaitingBusinessDays: number;
  updatedAt: string;
}

interface StaleWaitingRun {
  ranAt: string;
  enabled: boolean;
  thresholdBusinessDays: number;
  examined: number;
  nudged: Array<{ ticketId: string; displayNumber: number; subject: string }>;
  capped: boolean;
  error: string | null;
  trigger: 'schedule' | 'manual';
}

interface SettingsResponse {
  settings: Settings;
  staleWaitingLastRun: StaleWaitingRun | null;
}

export function AdminSettingsPage(): React.ReactElement {
  useDocumentTitle('Settings');
  const { agent } = useAuth();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [ticketingEmail, setTicketingEmail] = useState('');
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<TicketStatus>>(new Set());
  const [staleDays, setStaleDays] = useState('2');
  const [staleRun, setStaleRun] = useState<StaleWaitingRun | null>(null);
  const [staleBusy, setStaleBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function applyResponse(r: SettingsResponse) {
    setSettings(r.settings);
    setTicketingEmail(r.settings.ticketingEmail ?? '');
    setHiddenStatuses(new Set(r.settings.hiddenTicketStatuses));
    setStaleDays(String(r.settings.staleWaitingBusinessDays));
    setStaleRun(r.staleWaitingLastRun);
  }

  async function load() {
    try {
      applyResponse(await apiFetch<SettingsResponse>('/api/admin/settings'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load settings.');
    }
  }

  async function saveStalePolicy(patch: {
    staleWaitingEnabled?: boolean;
    staleWaitingBusinessDays?: number;
  }) {
    setStaleBusy(true);
    setInfo(null);
    setError(null);
    try {
      applyResponse(
        await apiFetch<SettingsResponse>('/api/admin/settings', {
          method: 'PATCH',
          json: patch,
        }),
      );
      setInfo('Follow-up rule saved.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
      // Put the input back to the stored value — a rejected number must
      // not sit in the box looking like it took.
      if (settings) setStaleDays(String(settings.staleWaitingBusinessDays));
    } finally {
      setStaleBusy(false);
    }
  }

  async function runStaleSweep() {
    setStaleBusy(true);
    setInfo(null);
    setError(null);
    try {
      const r = await apiFetch<{ result: StaleWaitingRun }>(
        '/api/admin/settings/stale-waiting/run',
        { method: 'POST' },
      );
      setStaleRun(r.result);
      if (r.result.error) setError(`Sweep failed: ${r.result.error}`);
      else if (!r.result.enabled) setInfo('Rule is switched off — nothing was changed.');
      else if (r.result.nudged.length === 0) setInfo('No tickets were overdue a follow-up.');
      else setInfo(`Reopened ${r.result.nudged.length} ticket(s) for follow-up.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sweep failed.');
    } finally {
      setStaleBusy(false);
    }
  }

  async function toggleHidden(status: TicketStatus, hide: boolean) {
    setStatusBusy(true);
    setInfo(null);
    setError(null);
    const next = new Set(hiddenStatuses);
    if (hide) next.add(status);
    else next.delete(status);
    try {
      const r = await apiFetch<SettingsResponse>('/api/admin/settings', {
        method: 'PATCH',
        json: { hiddenTicketStatuses: [...next] },
      });
      applyResponse(r);
      invalidateHiddenStatuses();
      setInfo(hide ? 'Status hidden.' : 'Status restored.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setStatusBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setInfo(null);
    setError(null);
    try {
      const r = await apiFetch<SettingsResponse>('/api/admin/settings', {
        method: 'PATCH',
        json: { ticketingEmail: ticketingEmail.trim() || null },
      });
      applyResponse(r);
      setInfo('Saved.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">Settings</h1>
        <p className="mt-1 text-[var(--color-muted)]">App-wide knobs that don't fit on the
          per-integration pages.</p>
      </div>

      {info && (
        <Card className="border-green-300 bg-green-50/40">
          <p className="text-sm text-green-900">{info}</p>
        </Card>
      )}
      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Ticketing email</h2>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          The address shown to portal users as the support / contact email and treated as the
          canonical ticketing identity in admin pages. Update without redeploy.
        </p>
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          Note: this is only the <strong>display string</strong> shown to portal clients.
          The actual mailbox the system reads inbound from and sends outbound as is the
          one connected at <code>/admin/google</code>. If you change the mailbox identity,
          reconnect Google there too — the OAuth-connected account drives both inbound
          and outbound.
        </p>
        <form onSubmit={save} className="mt-4 space-y-3">
          <FormField
            label="Email address"
            name="ticketingEmail"
            type="email"
            placeholder="ticketing@t3techpartners.com"
            value={ticketingEmail}
            onChange={(e) => setTicketingEmail(e.target.value)}
            maxLength={254}
            required
          />
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </form>
        {settings?.updatedAt && (
          <p className="mt-3 text-xs text-[var(--color-muted)]">
            Last updated {new Date(settings.updatedAt).toLocaleString()}
          </p>
        )}
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">
          No-response follow-up
        </h2>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          A ticket sitting at <strong>Waiting on client</strong> isn't in anyone's queue, so a
          client who never replies leaves it stalled indefinitely. When the wait passes the
          window below, the ticket goes back to <strong>Open</strong> and picks up a private
          note asking the assigned tech to make contact — by phone for preference. The client
          is not emailed: they've already stopped answering email.
        </p>
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          The window counts business time only — 08:00–18:00 ET, Monday to Friday, minus
          company holidays. A reply at 4pm Monday is followed up at 4pm Wednesday; one sent
          Friday evening starts counting Monday morning. A client reply stops the clock, and
          the next public reply from a tech starts a fresh one.
        </p>

        <div className="mt-4 space-y-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-[var(--color-body)]">
            <input
              type="checkbox"
              checked={settings?.staleWaitingEnabled ?? false}
              disabled={staleBusy || !settings}
              onChange={(e) => void saveStalePolicy({ staleWaitingEnabled: e.target.checked })}
              className="h-4 w-4 cursor-pointer accent-[var(--color-t3-blue)]"
            />
            Reopen tickets the client hasn't answered
          </label>

          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-[var(--color-body)]">
              <span className="mb-1 block font-medium">Wait before following up</span>
              <span className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={10}
                  step={1}
                  value={staleDays}
                  disabled={staleBusy || !settings?.staleWaitingEnabled}
                  onChange={(e) => setStaleDays(e.target.value)}
                  onBlur={() => {
                    const n = Number(staleDays);
                    if (!Number.isInteger(n) || n < 1 || n > 10) {
                      setStaleDays(String(settings?.staleWaitingBusinessDays ?? 2));
                      return;
                    }
                    if (n !== settings?.staleWaitingBusinessDays) {
                      void saveStalePolicy({ staleWaitingBusinessDays: n });
                    }
                  }}
                  className="w-20 rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-2 text-sm"
                />
                <span className="text-sm text-[var(--color-muted)]">business days (1–10)</span>
              </span>
            </label>
            <Button type="button" variant="outline" disabled={staleBusy} onClick={() => void runStaleSweep()}>
              {staleBusy ? 'Checking…' : 'Check now'}
            </Button>
          </div>
        </div>

        <div className="mt-4 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
          <p className="text-xs font-semibold text-[var(--color-header)]">Last check</p>
          {!staleRun && (
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              Hasn't run since the app last started. It runs automatically every hour — or
              press <strong>Check now</strong>.
            </p>
          )}
          {staleRun && (
            <div className="mt-1 space-y-1 text-xs text-[var(--color-muted)]">
              <p>
                {new Date(staleRun.ranAt).toLocaleString()} ·{' '}
                {staleRun.trigger === 'manual' ? 'run by hand' : 'scheduled'}
              </p>
              {staleRun.error ? (
                <p className="text-[var(--color-danger)]">Failed: {staleRun.error}</p>
              ) : !staleRun.enabled ? (
                <p>Rule was switched off — nothing checked.</p>
              ) : (
                <>
                  <p>
                    Examined {staleRun.examined} waiting ticket(s); reopened{' '}
                    {staleRun.nudged.length}.
                    {staleRun.capped && ' Hit the per-run limit — the rest follow next hour.'}
                  </p>
                  {staleRun.nudged.length > 0 && (
                    <ul className="list-inside list-disc">
                      {staleRun.nudged.slice(0, 10).map((n) => (
                        <li key={n.ticketId}>
                          <Link
                            to={`/tickets/${n.ticketId}`}
                            className="text-[var(--color-t3-blue)] hover:underline"
                          >
                            T-{n.displayNumber}
                          </Link>{' '}
                          {n.subject}
                        </li>
                      ))}
                      {staleRun.nudged.length > 10 && (
                        <li>…and {staleRun.nudged.length - 10} more.</li>
                      )}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Ticket statuses</h2>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Hide statuses you don't want agents to assign. A hidden status that is still the
          current status of an existing ticket remains visible in that ticket's dropdown so
          you can transition off it — but it won't appear elsewhere. Toggle anything off to
          restore it everywhere.
        </p>
        <ul className="mt-4 divide-y divide-[var(--color-border)] rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          {TICKET_STATUSES.map((s) => {
            const hidden = hiddenStatuses.has(s);
            return (
              <li key={s} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <StatusBadge status={s}>{s.replace(/_/g, ' ')}</StatusBadge>
                  {hidden && (
                    <span className="text-xs text-[var(--color-muted)]">hidden from dropdown</span>
                  )}
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-[var(--color-body)]">
                  <input
                    type="checkbox"
                    checked={hidden}
                    disabled={statusBusy}
                    onChange={(e) => void toggleHidden(s, e.target.checked)}
                    className="h-4 w-4 cursor-pointer accent-[var(--color-t3-blue)]"
                  />
                  Hide
                </label>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
